import { useQuery } from "@tanstack/react-query"
import { getRevenueConfig } from "@/lib/api/api"
import { getGoogleAdsConnections, getMetaConnections } from "@/lib/api/oauth"
import { logConnectorDegradation } from "@/lib/observability/connectors"
import type { RevenueConnectorRow } from "./OnboardingRevenueStep"

/**
 * Whether Stripe, Meta Ads and Google Ads are already connected for the
 * property, for the wizard's revenue step. Each source fails soft to "not
 * connected" (and logs the degradation): a failed status read must never hide
 * the Connect button.
 */
export function useRevenueConnections(
  propertyId: string | null,
  token: string | null,
  enabled: boolean,
): RevenueConnectorRow[] {
  const query = useQuery({
    queryKey: ["onboarding-revenue-connections", propertyId],
    enabled: Boolean(enabled && propertyId && token),
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      const pid = propertyId as string
      const tk = token as string
      const soft = (connectorId: string) => (error: unknown) => {
        logConnectorDegradation("connections_unavailable", { propertyId: pid }, {
          connectorId,
          reason: error instanceof Error ? error.message : "unknown",
        })
        return null
      }
      const [revenue, meta, googleAds] = await Promise.all([
        getRevenueConfig(pid, tk).catch(soft("revenue-config")),
        getMetaConnections(pid, tk).catch(soft("meta")),
        getGoogleAdsConnections(pid, tk).catch(soft("google-ads")),
      ])
      const setup = revenue?.revenueSetup ?? revenue
      return {
        stripe: Boolean(setup?.stripe?.configured),
        meta: (meta?.length ?? 0) > 0,
        googleAds: (googleAds?.length ?? 0) > 0,
      }
    },
  })

  const data = query.data
  const loading = !data && query.isLoading
  return [
    { id: "stripe", connected: loading ? null : Boolean(data?.stripe) },
    { id: "meta", connected: loading ? null : Boolean(data?.meta) },
    { id: "google-ads", connected: loading ? null : Boolean(data?.googleAds) },
  ]
}
